// 261023 E3-P3h1 (H3) — dòng «Kiểm trước khi bấm» ngay dưới nút chính của dải: ✓ ổn · ! lưu ý (KHÔNG chặn) · ✗ chặn (nút mờ, lý do ghi sẵn).
// THUẦN, không antd/react. CHỈ dùng dữ liệu đã có ở workbench; mục nào không có nguồn thật thì KHÔNG hiện (README Rev 2.1: các câu minh hoạ phải đối chiếu hành vi thật).
// Số tiền không bao giờ xuất hiện ở đây (K13).
import type { WorkbenchEpisode, WorkbenchView } from '../../../types/adaptationWorkbench';
import { friendlyReasonText } from './jobErrorText';
import type { NsKind } from './newStoryRunView';
import { START_BLOCK_TEXT } from './newStoryRunView';
import type { PushStrip } from './handoffBatchView';
import { isPushable } from './handoffBatchView';
import { SKELETON_GATE_ERROR_LINE, SKELETON_GATE_STALE_LINE } from './skeletonGateView';
import type { WorkbenchTarget } from './workbenchView';

export type CkSign = 'ok' | 'warn' | 'bad';
export interface CkLink { label: string; target?: WorkbenchTarget; action?: 'openReview' }
export interface CkItem { s: CkSign; t: string; link?: CkLink }
export interface Preflight { items: CkItem[]; /** Toàn ✓ ⇒ gập thành «✓ Đủ điều kiện chạy tiếp ▸ xem N mục». */ allOk: boolean; summary: string }

export interface PreflightCtx {
  /** Lý do khoá nút «Duyệt & chạy tiếp» (khung xương/tổng quan) — `nsApproveBlock`. */
  approveBlock?: string | null;
  /** Lý do khoá nút khác (vd chưa tính được chi phí) — chữ, KHÔNG số tiền. */
  disabledReason?: string | null;
  push?: PushStrip | null;
  /** Tập còn tên gốc rò theo xem thử đẩy (`HandoffBatchPreview.episodes[].leaks`). */
  leakEpisodes?: number[];
  /** Lý do máy chưa tự chốt dàn tập (`planStopReasonLines`). */
  planReasons?: string[];
}

const RANK: Record<CkSign, number> = { bad: 0, warn: 1, ok: 2 };
const finish = (items: CkItem[]): Preflight => {
  const sorted = items.map((x, i) => ({ x, i })).sort((a, b) => RANK[a.x.s] - RANK[b.x.s] || a.i - b.i).map((o) => o.x);   // mục chặn đứng trước, ổn định
  const allOk = sorted.length > 0 && sorted.every((i) => i.s === 'ok');
  return { items: sorted, allOk, summary: allOk ? `✓ Đủ điều kiện chạy tiếp · ${sorted.length} mục` : '' };
};
const none: Preflight = { items: [], allOk: false, summary: '' };

const epList = (n: number[]): string => n.join(', ');
const FALLBACK_START = 'Chưa bắt đầu được — xem lý do ở dải';

export const preflightOf = (kind: NsKind, w: WorkbenchView | null | undefined, ctx: PreflightCtx): Preflight => {
  if (!w) return none;
  const items: CkItem[] = [];
  const raw = w.outlineFlags?.rawChapters ?? 0;

  if (kind === 'start' || kind === 'start_blocked') {
    if (kind === 'start_blocked') {
      const why = w.canStartNewStory?.reason ? START_BLOCK_TEXT[w.canStartNewStory.reason] : undefined;
      items.push({ s: 'bad', t: why ?? FALLBACK_START });
    }
    if (raw > 0) items.push({ s: 'ok', t: `Đã nạp ${raw} chương` });
    if (kind === 'start') items.push({ s: 'ok', t: 'Chạy nền, rời trang được' });
    return finish(items);
  }

  if (kind === 'wait_skeleton') {
    if (ctx.approveBlock) items.push({ s: 'bad', t: ctx.approveBlock });
    else if (ctx.disabledReason) items.push({ s: 'bad', t: ctx.disabledReason });
    const sk = w.stages?.find((s) => s.key === 'skeleton');
    const attn = sk?.attention.length ?? 0;
    if (attn > 0) items.push({ s: 'warn', t: `${attn} cảnh báo khung xương, không chặn`, link: { label: 'Xem', target: { stage: 'skeleton' } } });
    const g = w.skeletonGate;
    if (g && g.state !== 'MISSING') {
      if (g.state === 'STALE') items.push({ s: 'warn', t: SKELETON_GATE_STALE_LINE });
      else if (g.stopReason === 'GATE_ERROR') items.push({ s: 'warn', t: SKELETON_GATE_ERROR_LINE });
      else if (g.unresolved.length > 0) items.push({ s: 'warn', t: `Máy chấm khung xương còn ${g.unresolved.length} việc bạn có thể xem`, link: { label: 'Xem', target: { stage: 'skeleton' } } });
      else items.push({ s: 'ok', t: 'Khung xương đã được máy chấm, không còn việc' });
    }
    if (w.overviewState?.exists) items.push({ s: 'ok', t: 'Tổng quan truyện đã tạo' });
    return finish(items);
  }

  if (kind === 'wait_plan') {
    (ctx.planReasons ?? []).forEach((t) => items.push({ s: 'warn', t, link: { label: 'Xem', target: { stage: 'split' } } }));
    return finish(items);
  }

  if (kind === 'done') {
    const push = ctx.push;
    if (!push || push.mode === 'legacy' || push.mode === 'running' || push.mode === 'allPushed' || push.mode === 'partial' || push.mode === 'calculating') return none;
    if (push.disabled && push.disabledReason && (push.mode === 'none' || push.mode === 'nothing')) items.push({ s: 'bad', t: push.disabledReason });
    if (w.handoff?.readiness === 'blocked' && w.handoff.blockers[0]) items.push({ s: 'bad', t: friendlyReasonText(w.handoff.blockers[0].message) });
    const eps: WorkbenchEpisode[] = w.episodes ?? [];
    const stuck = eps.filter((e) => e.written === 'draft' && e.handoff !== 'up_to_date' && !isPushable(e)).map((e) => e.ep);
    if (stuck.length > 0) items.push({ s: 'warn', t: `Tập ${epList(stuck)} còn lỗi nặng hoặc đang bị chặn — sẽ không được đẩy lần này`, link: { label: 'Xem trong Sổ duyệt', action: 'openReview' } });
    eps.filter((e) => isPushable(e) && e.criticErrors > 0).forEach((e) => items.push({ s: 'warn', t: `Tập ${e.ep} còn ${e.criticErrors} lỗi — không chặn đẩy`, link: { label: 'Xem', action: 'openReview' } }));
    if ((ctx.leakEpisodes ?? []).length > 0) items.push({ s: 'warn', t: `Tên gốc rò ở tập ${epList(ctx.leakEpisodes!)}: máy sẽ dừng hỏi bạn khi đẩy` });
    if (w.episodePlan?.state === 'APPROVED') items.push({ s: 'ok', t: 'Dàn tập đã chốt' });
    const written = eps.filter((e) => e.written === 'draft').length;
    if (written > 0) items.push({ s: 'ok', t: `Đã viết ${written}/${w.outlineFlags?.episodesListed || eps.length} tập` });
    return finish(items);
  }

  return none;
};
